import ReactQuill, { Quill } from "react-quill";
import Emoji from "quill-emoji";
import { useRef, useState, useMemo, useEffect } from "react";
import { withSnackbar } from "../../utils/SnackbarHOC";
import axiosInstance from "../../utils/API";

import "quill-paste-smart";
import "react-quill/dist/quill.snow.css";
import "quill-emoji/dist/quill-emoji.css";
import "./style.css";

Quill.register("modules/emoji", Emoji);
const BlockEmbed = Quill.import("blots/block/embed");
const Delta = Quill.import("delta");

class SecureImageBlot extends BlockEmbed {
  static blotName = "secureImage";
  static tagName = "img";

  static create(value) {
    const node = super.create();
    node.setAttribute("data-file-id", value?.id || "");
    node.setAttribute("alt", value?.name || "image");
    node.setAttribute("style", "max-width:100%; height:auto; display:block;");

    if (value?.src) node.setAttribute("src", value.src);

    return node;
  }

  static value(node) {
    return {
      id: node.getAttribute("data-file-id") || "",
      name: node.getAttribute("alt") || "image",
    };
  }
}

Quill.register(SecureImageBlot);
const icons = Quill.import("ui/icons");
icons.attachment = `
<svg viewBox="0 0 18 18">
  <path
    d="M5.5 9.5l5.7-5.7a3 3 0 1 1 4.2 4.2l-7.1 7.1a4.5 4.5 0 0 1-6.4-6.4l7.1-7.1"
    stroke="currentColor"
    stroke-width="1.5"
    fill="none"
    stroke-linecap="round"
  />
</svg>
`;

const extractFilename = (contentDisposition) => {
  if (!contentDisposition) return null;
  const match =
    contentDisposition.match(/filename\*=(?:UTF-8'')?([^;]+)/i) ||
    contentDisposition.match(/filename="?([^"]+)"?/i);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]).replace(/(^"|"$)/g, "");
  } catch {
    return match[1].replace(/(^"|"$)/g, "");
  }
};

export const sanitizeMessageForStorage = (html = "") => {
  if (!html) return html;

  return html.replace(
    /<img([^>]*data-file-id=["'][^"']+["'][^>]*?)\s+src=["']blob:[^"']+["']([^>]*?)>/gi,
    "<img$1$2>"
  );
};

function EditorCustom({
  value,
  handleSubmit = () => {},
  setValue,
  snackbarShowMessage,
  enableOnBlur,
  ...rest
}) {
  const [readOnly, setReadOnly] = useState(false);
  const [isPasting, setIsPasting] = useState(false);
  const [text, setText] = useState(value);

  const quillRef = useRef(null);
  const fileInputRef = useRef(null);
  const createdBlobUrlsRef = useRef([]);

  const handleBlur = () => {
    if (!isPasting) {
      setReadOnly(true);
      handleSubmit(text);
    }
  };

  const handleKey = (e, state) => {
    if (e.key === "Control" || e.key === "Meta") setIsPasting(state);
  };
  const modules = useMemo(
    () => ({
      toolbar: {
        container: [["bold", "italic", "underline", "link", "emoji", "attachment"]],
        handlers: {
          attachment: () => fileInputRef.current?.click(),
        },
      },
      clipboard: {
        matchVisual: false,
        matchers: [
          [
            "img",
            (node, delta) => {
              const id = node.getAttribute("data-file-id");
              if (!id) return delta || new Delta();

              const name = node.getAttribute("alt") || "image";
              return new Delta().insert({ secureImage: { id, name } });
            },
          ],
        ],
      },
      "emoji-toolbar": true,
      "emoji-shortname": true,
    }),
    []
  );

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("categorie", "emailtype");

    const { data } = await axiosInstance.post(
      "/candidatsmanagement/uploaded-files/add",
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );

    return data;
  };

  const secureOpenById = async (id, filenameFallback) => {
    const res = await axiosInstance.get(`/candidatsmanagement/downloadfile/${id}`, {
      responseType: "blob",
    });

    const contentType = res.headers?.["content-type"] || "";
    const dispo = res.headers?.["content-disposition"] || "";
    const filename = extractFilename(dispo) || filenameFallback || "download";

    const blob = new Blob([res.data], { type: contentType });
    const blobUrl = URL.createObjectURL(blob);

    const canPreview =
      contentType.startsWith("image/") || contentType === "application/pdf";

    if (canPreview) {
      window.open(blobUrl, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
      return;
    }

    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  };

  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    try {
      snackbarShowMessage("Upload en cours...", "warning", null);

      const data = await uploadFile(file);
      const id = data?.id;
      if (!id) throw new Error("No id returned by server");

      const quill = quillRef.current?.getEditor();
      if (!quill) throw new Error("Éditeur non disponible");
      const range = quill.getSelection(true);
      const insertAt = range ? range.index : quill.getLength();

      if (file.type?.startsWith("image/")) {
        const res = await axiosInstance.get(
          `/candidatsmanagement/downloadfile/${id}?inline=1`,
          { responseType: "blob" }
        );

        const blobUrl = URL.createObjectURL(res.data);
        createdBlobUrlsRef.current.push(blobUrl);

        quill.insertEmbed(insertAt, "secureImage", {
          id,
          name: file.name,
          src: blobUrl,
        });

        quill.insertText(insertAt + 1, "\n");

        const linkText = file.name;
        quill.insertText(insertAt + 2, linkText, { link: `#download:${id}` });
        quill.insertText(insertAt + 2 + linkText.length, "\n");
        quill.setSelection(insertAt + 3 + linkText.length);

        snackbarShowMessage("Image ajoutée", "success", 2500);
        return;
      }

      quill.insertText(insertAt, file.name, { link: `#download:${id}` });
      quill.insertText(insertAt + file.name.length, " ");
      quill.setSelection(insertAt + file.name.length + 1);

      snackbarShowMessage("Pièce jointe ajoutée ✅", "success", 3000);
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Erreur upload";
      snackbarShowMessage(`Erreur: ${msg}`, "error", 4000);
    }
  };

  const onEditorClickCapture = async (e) => {
    const a = e.target?.closest?.("a");
    if (!a) return;

    const href = a.getAttribute("href") || "";
    if (!href.startsWith("#download:")) return;

    e.preventDefault();
    e.stopPropagation();

    const id = href.replace("#download:", "");
    try {
      await secureOpenById(id, a.textContent?.trim());
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Erreur téléchargement";
      snackbarShowMessage(`Erreur: ${msg}`, "error", 4000);
    }
  };

  const hydrateImages = async () => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const root = quill.root;
    const imgs = Array.from(root.querySelectorAll("img[data-file-id]"));

    for (const img of imgs) {
      const id = img.getAttribute("data-file-id");
      const src = img.getAttribute("src") || "";
      if (src && (src.startsWith("blob:") || src.startsWith("http"))) continue;

      try {
        const res = await axiosInstance.get(
          `/candidatsmanagement/downloadfile/${id}?inline=1`,
          { responseType: "blob" }
        );
        const blobUrl = URL.createObjectURL(res.data);
        createdBlobUrlsRef.current.push(blobUrl);
        img.setAttribute("src", blobUrl);
      } catch {
      }
    }
  };
  useEffect(() => {
    if (enableOnBlur) setText(value);
  }, [value]);
  useEffect(() => {
    const t = setTimeout(() => hydrateImages(), 200);
    return () => clearTimeout(t);
  }, [value]);

  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const toolbar = editor.getModule("toolbar");
    if (!toolbar?.container) return;
    const labels = {
      bold: "Mettre en gras",
      italic: "Mettre en italique",
      underline: "Souligner",
      link: "Insérer un lien hypertexte",
      emoji: "Insérer un émoji",
      attachment: "Ajouter une pièce jointe (image ou fichier)",
    };
    toolbar.container.querySelectorAll("button[class]").forEach((btn) => {
      const cls = Array.from(btn.classList).find((c) => c.startsWith("ql-"));
      if (!cls) return;
      const key = cls.replace("ql-", "");
      if (labels[key]) btn.setAttribute("title", labels[key]);
    });
  }, []);

  useEffect(() => {
    return () => {
      createdBlobUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
      createdBlobUrlsRef.current = [];
    };
  }, []);

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: "none" }}
        onChange={onFilePicked}
      />

      <div onClickCapture={onEditorClickCapture}>
        <ReactQuill
          ref={quillRef}
          modules={modules}
          theme="snow"
          readOnly={readOnly}
          value={enableOnBlur ? text : value}
          onBlur={enableOnBlur ? handleBlur : () => {}}
          onKeyDown={(e) => handleKey(e, true)}
          onKeyUp={(e) => handleKey(e, false)}
          onChange={(html) => {
            if (enableOnBlur) setText(html);
            else setValue(html);
          }}
          placeholder="Saisie le text ici..."
          {...rest}
        />
      </div>
    </>
  );
}

export default withSnackbar(EditorCustom);
